/* redesign-comparison.js — the share module on
   /pages/european-baby-formula-comparison-chart.

   THE MODULE IS MERCHANT CONTENT. It lives inside {{ page.content }}, with its
   own inline stylesheet, and nothing in this file edits that field — the
   markup, the copy and the embed codes are read and rearranged at runtime,
   never rewritten.

   IT ALSO ARRIVED UNWIRED. Its tabs carry `role="tab"` and ids
   (ols-tab-embed / -link / -share), its panels the matching
   ols-panel-embed / -link / -share, and its five Copy buttons the matching
   ols-copy-* / ols-code-* pairs — but NO script on the page ever bound any of
   them. Verified on production and on the preview: clicking a tab changes
   nothing, so two of the three panels are unreachable, and every Copy button
   copies an empty string. The author's naming convention is intact, so this
   wires it the way it was plainly meant to work rather than inventing a shape.

   AND IT WAS THE LOUDEST THING ON THE PAGE. Live renders a green gradient
   hero, three tabs and an open code panel, all permanently expanded, above the
   charts people actually came for. The handoff draws it collapsed — one quiet
   cream card, 106px tall, that opens on demand. Sam's call: lean into that,
   lose no functionality.

   The open/close itself is NOT implemented here. The row is built to the
   site's own accordion contract (.rd-faq__row / __btn / __body + aria), and
   redesign.js binds one delegated listener per [data-rd-accordion] container
   at load — the attribute sits on the page wrapper in the template, so a row
   built afterwards is picked up by delegation and inherits the same animation
   and behaviour as every other accordion on the site. */

(function () {
  'use strict';

  var root = document.querySelector('.rd-cmp');
  if (!root) return;
  var embed = root.querySelector('.ols-embed');
  if (!embed) return;

  /* ------------------------------------------------- 1. wire the tabs */

  function wireTabs() {
    var tabs = embed.querySelectorAll('.ols-embed-tab');
    if (!tabs.length) return;

    Array.prototype.forEach.call(tabs, function (tab) {
      /* `ols-tab-embed` -> `ols-panel-embed`. Derived from the id rather than
         from position, so re-ordering the tabs in the field cannot silently
         pair a tab with the wrong panel. */
      var key = (tab.id || '').replace(/^ols-tab-/, '');
      var panel = key && document.getElementById('ols-panel-' + key);
      if (!panel) return;

      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', tab.classList.contains('active') ? 'true' : 'false');
      if (!tab.hasAttribute('type')) tab.type = 'button';

      tab.addEventListener('click', function () {
        Array.prototype.forEach.call(tabs, function (t) {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        Array.prototype.forEach.call(embed.querySelectorAll('.ols-embed-panel'), function (p) {
          p.classList.remove('active');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        panel.classList.add('active');
      });
    });
  }

  /* ------------------------------------------------ 2. wire the copying */

  function wireCopy() {
    var buttons = embed.querySelectorAll('.ols-embed-copy');

    Array.prototype.forEach.call(buttons, function (btn) {
      /* `ols-copy-full` -> `ols-code-full`. Falls back to the nearest code box
         so a button added later without a matching id still works. */
      var key = (btn.id || '').replace(/^ols-copy-/, '');
      var field = (key && document.getElementById('ols-code-' + key)) ||
                  (btn.parentElement && btn.parentElement.querySelector('.ols-embed-code'));
      if (!field) return;

      if (!btn.hasAttribute('type')) btn.type = 'button';
      var label = btn.textContent;
      var restore;

      btn.addEventListener('click', function () {
        var text = field.value != null ? field.value : field.textContent;

        function done(ok) {
          clearTimeout(restore);
          btn.textContent = ok ? 'Copied' : 'Press ⌘C';
          btn.classList.toggle('is-copied', ok);
          restore = setTimeout(function () {
            btn.textContent = label;
            btn.classList.remove('is-copied');
          }, 1800);
        }

        /* The async API needs a secure context and can be refused by
           permissions policy; the selection fallback works everywhere and is
           what makes this reliable inside an iframe or on http. */
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () { done(true); }, fallback);
        } else {
          fallback();
        }

        function fallback() {
          try {
            field.removeAttribute('readonly');
            field.focus();
            field.select();
            field.setSelectionRange(0, text.length);
            var ok = document.execCommand('copy');
            field.setAttribute('readonly', 'readonly');
            done(ok);
          } catch (e) {
            /* Leave it selected so ⌘C still works, and say so on the button. */
            done(false);
          }
        }
      });
    });
  }

  /* --------------------------------------- 3. collapse it into a card row

     The hero's own two lines become the trigger. Their nodes are MOVED, not
     read as text, so the emoji and any markup the field contains survive. */

  function collapse() {
    var hero = embed.querySelector('.ols-embed-hero');
    var title = embed.querySelector('.ols-embed-hero-title');
    var sub = embed.querySelector('.ols-embed-hero-sub');
    if (!hero || !title) return;
    if (embed.querySelector('.rd-faq__btn')) return;   /* already built */

    var row = document.createElement('div');
    row.className = 'rd-faq__row rd-cmp__sharerow';

    var body = document.createElement('div');
    body.className = 'rd-faq__body rd-cmp__sharebody';
    body.id = 'rd-cmp-share-panel';
    body.hidden = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rd-faq__btn rd-cmp__sharebtn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', body.id);

    var text = document.createElement('span');
    text.className = 'rd-cmp__sharetext';

    var t = document.createElement('span');
    t.className = 'rd-faq__title';
    while (title.firstChild) t.appendChild(title.firstChild);
    text.appendChild(t);

    if (sub) {
      var s = document.createElement('span');
      s.className = 'rd-faq__lead';
      while (sub.firstChild) s.appendChild(sub.firstChild);
      text.appendChild(s);
    }

    var mark = document.createElement('span');
    mark.className = 'rd-faq__mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.innerHTML = '<span class="rd-faq__mark-plus">+</span>' +
                     '<span class="rd-faq__mark-minus">−</span>';

    btn.appendChild(text);
    btn.appendChild(mark);
    row.appendChild(btn);

    /* Everything that was in the module except the old hero moves into the
       panel, in its original order — tabs, all three panels and their copy
       controls, which keep the listeners bound above because moving a node
       does not detach them. */
    embed.insertBefore(row, hero);
    hero.parentNode.removeChild(hero);
    while (row.nextSibling) body.appendChild(row.nextSibling);
    row.appendChild(body);
  }

  wireTabs();
  wireCopy();
  collapse();
})();
